import { sanitizeHtml } from "@/lib/sanitize-html";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { format } from "date-fns";
import {
  CalendarCheck,
  Loader2,
  Sparkles,
  Clock,
  Calendar,
  ChevronDown,
  ChevronUp,
  Search,
  Star,
  ArrowRight,
  Download,
  GraduationCap,
} from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { registerForSession } from "@/lib/session-registration.functions";
import { getReferralSource } from "@/lib/referral-tracker";
import { getUpcomingRegistrationCount, listPastSessions } from "@/lib/live-sessions.functions";
import { castTopicVote, getPublicPoll } from "@/lib/topic-poll.functions";
import { buildIcs, downloadIcs } from "@/lib/ics";
import { trackEvent } from "@/lib/analytics";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/freevisasessions")({
  head: () => ({
    meta: [
      { title: "Free F-1 Visa Masterclass & Live Mock Drills - WeLivdIt" },
      {
        name: "description",
        content:
          "Free F-1 visa masterclass every Sunday at 7:00 PM IST on Google Meet. Live DS-160 audits, 214(b) refusal breakdowns and open Q&A with senior visa coaches.",
      },
      { property: "og:title", content: "Free F-1 Visa Masterclass & Live Mock Drills - WeLivdIt" },
      {
        property: "og:description",
        content: "Real profile teardowns, DS-160 audits and mock interview drills. 100% free.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://welivdit.com/freevisasessions" },
      { property: "og:image", content: "https://welivdit.com/og-share-v6.jpg" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { name: "twitter:image", content: "https://welivdit.com/og-share-v6.jpg" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://welivdit.com/freevisasessions" }],
  }),
  component: FreeVisaSessionsPage,
});

type SessionRow = {
  id: string;
  title: string;
  description: string | null;
  starts_at: string | null;
  duration_minutes: number;
  status: "planning" | "scheduled";
  join_url: string | null;
  registration_link: string | null;
  is_published: boolean;
  created_at: string;
};

function stripArchiveTag(desc: string | null): string {
  if (!desc) return "";
  return desc.replace(/^\[archive:[^\]]+\]\s*/, "");
}

/** Labels always rendered in IST, regardless of the viewer's timezone. */
function istDate(d: Date): string {
  return new Intl.DateTimeFormat("en-IN", {
    weekday: "long",
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "Asia/Kolkata",
  }).format(d);
}
function istTime(d: Date): string {
  return new Intl.DateTimeFormat("en-IN", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone: "Asia/Kolkata",
  })
    .format(d)
    .toUpperCase();
}

/** Next Sunday 7:00 PM IST (19:00 +05:30 => 13:30 UTC), as an ISO string. */
function nextSunday7pmIst(from: Date = new Date()): string {
  const d = new Date(Date.UTC(from.getUTCFullYear(), from.getUTCMonth(), from.getUTCDate(), 13, 30, 0, 0));
  while (d.getTime() <= from.getTime() || d.getUTCDay() !== 0) {
    d.setUTCDate(d.getUTCDate() + 1);
  }
  return d.toISOString();
}

function Countdown({ targetIso, onExpire }: { targetIso: string | null; onExpire?: () => void }) {
  const [now, setNow] = useState<number | null>(null);
  useEffect(() => {
    setNow(Date.now());
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const sync = () => setNow(Date.now());
    window.addEventListener("focus", sync);
    document.addEventListener("visibilitychange", sync);
    return () => {
      window.removeEventListener("focus", sync);
      document.removeEventListener("visibilitychange", sync);
    };
  }, []);

  const expired = now !== null && targetIso !== null && new Date(targetIso).getTime() - now <= 0;
  useEffect(() => {
    if (expired) onExpire?.();
  }, [expired, onExpire]);

  if (!targetIso) return null;
  const remaining = now === null ? null : Math.max(0, new Date(targetIso).getTime() - now);
  const dd = remaining === null ? null : Math.floor(remaining / 86_400_000);
  const hh = remaining === null ? null : Math.floor((remaining % 86_400_000) / 3_600_000);
  const mm = remaining === null ? null : Math.floor((remaining % 3_600_000) / 60_000);
  const ss = remaining === null ? null : Math.floor((remaining % 60_000) / 1000);
  const pad = (n: number | null) => (n === null ? "--" : n.toString().padStart(2, "0"));

  const units: { v: string; l: string }[] = [
    { v: pad(dd), l: "Days" },
    { v: pad(hh), l: "Hours" },
    { v: pad(mm), l: "Mins" },
    { v: pad(ss), l: "Secs" },
  ];
  return (
    <div className="flex items-center justify-center gap-2 sm:gap-3" aria-label="Time until the next live session">
      {units.map((u, i) => (
        <div key={u.l} className="flex items-center gap-2 sm:gap-3">
          <div className="rounded-xl border border-border bg-background px-3 py-2 min-w-[62px] text-center shadow-sm">
            <div className="font-mono text-xl sm:text-2xl font-bold tabular-nums text-primary">{u.v}</div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{u.l}</div>
          </div>
          {i < units.length - 1 && <span className="text-lg font-bold text-muted-foreground">:</span>}
        </div>
      ))}
    </div>
  );
}

function FreeVisaSessionsPage() {
  const [loading, setLoading] = useState(true);
  const [upcoming, setUpcoming] = useState<SessionRow | null>(null);
  const [previous, setPrevious] = useState<SessionRow[]>([]);

  // Search & Filter State
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTag, setSelectedTag] = useState<string>("All");
  const [question, setQuestion] = useState("");

  // Live, public registration count for the upcoming session.
  const [registeredCount, setRegisteredCount] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const nowIso = new Date().toISOString();
      const [scheduledRes, planningRes, archiveRes] = await Promise.all([
        supabase
          .from("live_sessions")
          .select("*")
          .eq("is_published", true)
          .eq("status", "scheduled")
          .gte("starts_at", nowIso)
          .order("starts_at", { ascending: true })
          .limit(1),
        supabase
          .from("live_sessions")
          .select("*")
          .eq("is_published", true)
          .eq("status", "planning")
          .order("created_at", { ascending: false })
          .limit(1),
        listPastSessions(),
      ]);

      if (cancelled) return;

      const up = (scheduledRes.data?.[0] ?? planningRes.data?.[0] ?? null) as SessionRow | null;
      setUpcoming(up);
      setPrevious(
        (archiveRes ?? []).map((s) => ({
          id: s.id,
          title: s.title,
          description: s.description,
          starts_at: s.starts_at,
        })) as unknown as SessionRow[],
      );
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const [fallbackIso, setFallbackIso] = useState<string | null>(null);
  const [registerOpen, setRegisterOpen] = useState(false);
  useEffect(() => {
    const sync = () => setFallbackIso(nextSunday7pmIst());
    sync();
    const t = setInterval(sync, 60_000);
    window.addEventListener("focus", sync);
    return () => {
      clearInterval(t);
      window.removeEventListener("focus", sync);
    };
  }, []);

  const rollForward = useCallback(() => {
    if (!upcoming?.starts_at) setFallbackIso(nextSunday7pmIst());
  }, [upcoming]);

  const countdownTarget = upcoming?.starts_at ?? fallbackIso;

  // Real-time registered count
  const upcomingId = upcoming?.id ?? null;
  useEffect(() => {
    let alive = true;
    const pull = () => {
      getUpcomingRegistrationCount({ data: { sessionId: upcomingId } })
        .then((r) => alive && setRegisteredCount(r.count))
        .catch(() => undefined);
    };
    pull();
    const t = setInterval(pull, 30_000);
    window.addEventListener("focus", pull);
    return () => {
      alive = false;
      clearInterval(t);
      window.removeEventListener("focus", pull);
    };
  }, [upcomingId]);

  const parsedUpcoming = useMemo(
    () => parseSessionDescription(stripArchiveTag(upcoming?.description ?? null)),
    [upcoming],
  );

  const sessionMeta = useMemo(() => {
    if (!upcoming) return null;
    const startsAt = upcoming.starts_at;
    const d = startsAt ? new Date(startsAt) : null;
    return {
      id: upcoming.id,
      title: upcoming.title,
      startsAt,
      dateLabel: d ? istDate(d) : "This Sunday",
      timeLabel: d ? `${istTime(d)} IST` : "7:00 PM IST",
      joinUrl: upcoming.join_url ?? upcoming.registration_link ?? null,
      durationMinutes: upcoming.duration_minutes || 90,
      topics: parsedUpcoming.bullets.slice(0, 3),
    };
  }, [upcoming, parsedUpcoming]);

  // Filtered Previous Sessions (live, as the user types)
  const filteredPrevious = useMemo(() => {
    const terms = searchQuery.toLowerCase().trim().split(/\s+/).filter(Boolean);
    return previous.filter((s) => {
      const title = s.title.toLowerCase();
      const desc = (s.description || "").toLowerCase();
      const haystack = `${title} ${desc} ${sessionCategory(s).toLowerCase()}`;

      const matchesSearch = terms.every((t) => haystack.includes(t));

      let matchesTag = true;
      if (selectedTag === "Refusal Recovery") {
        matchesTag = title.includes("refusal") || desc.includes("refusal") || title.includes("214(b)");
      } else if (selectedTag === "DS-160 Audits") {
        matchesTag = title.includes("ds-160") || desc.includes("ds-160") || desc.includes("form");
      } else if (selectedTag === "Funding & Loans") {
        matchesTag =
          title.includes("fund") || desc.includes("loan") || desc.includes("sponsor") || desc.includes("bank");
      } else if (selectedTag === "Mock Drills") {
        matchesTag = title.includes("mock") || desc.includes("drill") || desc.includes("simulation");
      }

      return matchesSearch && matchesTag;
    });
  }, [previous, searchQuery, selectedTag]);

  return (
    <main className="min-h-screen bg-background">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="mx-auto max-w-4xl px-5 py-16 md:py-24 text-center">
          <div className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-primary bg-primary/10 rounded-full px-3 py-1">
            <Sparkles className="h-3 w-3" /> 100% free · no paywalls
          </div>
          <h1 className="mt-5 font-display text-3xl sm:text-5xl md:text-6xl font-semibold tracking-tight">
            Master Your F1 Visa Interview — Free Weekly Live Sessions
          </h1>
          <p className="mt-5 text-base md:text-lg text-foreground/70 max-w-2xl mx-auto leading-relaxed">
            Join WeLivdIt visa experts live every week for real mock interviews, DS-160 breakdowns, and live Q&amp;A.
            100% Free.
          </p>

          {/* 3-COLUMN HERO STATS TRUST BAR */}
          <div className="mt-8 grid grid-cols-3 gap-3 md:gap-6 border-y border-border/70 py-6 max-w-2xl mx-auto text-center bg-card/40 rounded-2xl">
            <div className="px-2">
              <div className="font-display text-xl md:text-2xl font-bold text-primary">16+</div>
              <div className="text-[11px] md:text-xs text-muted-foreground font-medium mt-0.5">
                Free Sessions Completed
              </div>
            </div>
            <div className="px-2 border-x border-border/70">
              <div className="font-display text-xl md:text-2xl font-bold text-primary">1,500+</div>
              <div className="text-[11px] md:text-xs text-muted-foreground font-medium mt-0.5">
                Students Drilled Live
              </div>
            </div>
            <div className="px-2">
              <div className="font-display text-xl md:text-2xl font-bold text-primary flex items-center justify-center gap-1">
                4.7 <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
              </div>
              <div className="text-[11px] md:text-xs text-muted-foreground font-medium mt-0.5">
                Rating across Consulates
              </div>
            </div>
          </div>

          {/* LIVE BADGE + COUNTDOWN + UPCOMING SESSION CARD */}
          {sessionMeta && (
            <div className="mt-10 rounded-3xl border border-border bg-card p-6 md:p-8 shadow-sm text-left">
              <div className="flex flex-wrap justify-center gap-2">
                <span className="inline-flex items-center gap-2 rounded-full bg-rose-500/10 text-rose-600 dark:text-rose-400 px-4 py-1.5 text-[11px] sm:text-xs font-bold uppercase tracking-wider">
                  <span className="h-2 w-2 rounded-full bg-rose-500 animate-pulse" />
                  🔴 Live this Sunday at 7:00 PM IST
                </span>
                {registeredCount !== null && registeredCount > 0 && (
                  <span className="inline-flex items-center gap-2 rounded-full bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 px-4 py-1.5 text-[11px] sm:text-xs font-bold uppercase tracking-wider">
                    <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
                    {registeredCount.toLocaleString("en-IN")} {registeredCount === 1 ? "student" : "students"}{" "}
                    registered · live count
                  </span>
                )}
              </div>

              <div className="mt-5">
                <Countdown targetIso={countdownTarget} onExpire={rollForward} />
              </div>

              <div className="mt-7 rounded-2xl bg-muted/50 border border-border/70 p-5">
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Upcoming topic</div>
                <h2 className="mt-1 font-display text-xl md:text-2xl font-semibold">{sessionMeta.title}</h2>
                <div className="mt-3 grid gap-2 sm:grid-cols-2 text-sm">
                  <div className="flex items-center gap-2">
                    <CalendarCheck className="h-4 w-4 text-primary shrink-0" />
                    <span className="font-medium">
                      {upcoming?.starts_at
                        ? `${sessionMeta.dateLabel} · ${sessionMeta.timeLabel}`
                        : "This Sunday at 7:00 PM IST"}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-primary shrink-0" />
                    <span className="font-medium">{sessionMeta.durationMinutes} min live on Google Meet</span>
                  </div>
                </div>

                {parsedUpcoming.overview && (
                  <p className="mt-4 text-sm text-foreground/80 leading-relaxed">{parsedUpcoming.overview}</p>
                )}

                {parsedUpcoming.bullets.length > 0 && (
                  <div className="mt-4">
                    <p className="text-[11px] font-bold uppercase tracking-wider text-primary">
                      What we will cover live
                    </p>
                    <ul className="mt-2 space-y-2">
                      {parsedUpcoming.bullets.map((b, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-foreground/90">
                          <span className="mt-[2px] text-primary font-bold">✓</span>
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="mt-4 flex flex-wrap gap-2">
                  <span className="inline-flex items-start gap-2 rounded-full bg-primary/10 text-primary px-3.5 py-1.5 text-[11px] font-semibold">
                    <GraduationCap className="h-3.5 w-3.5 mt-[1px] shrink-0" />
                    Hosted by Senior Visa Coaches (Ex-Visa Officers &amp; US Alumni)
                  </span>
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-3.5 py-1.5 text-[11px] font-semibold text-muted-foreground">
                    💡 Join 5 mins early to test your audio
                  </span>
                </div>
              </div>

              <div className="mt-7 text-center">
                <Button
                  size="lg"
                  onClick={() => {
                    trackEvent("free_session_cta_click", {
                      location: "hero",
                      session_id: sessionMeta.id,
                      session_title: sessionMeta.title,
                    });
                    setRegisterOpen(true);
                  }}
                  className="w-full sm:w-auto rounded-full bg-gradient-cta text-primary-foreground px-10 py-7 text-base sm:text-lg font-bold shadow-glow hover:opacity-90 hover:scale-[1.02] transition"
                >
                  Reserve My Free Spot Now <ArrowRight className="ml-1 h-5 w-5" />
                </Button>
                <p className="mt-3 text-xs sm:text-sm font-medium text-foreground/70">
                  🔒 Instant Google Meet link &amp; calendar invite sent to your email upon registration.
                </p>
              </div>
            </div>
          )}
        </div>
      </section>

      {sessionMeta && (
        <RegistrationModal
          open={registerOpen}
          onOpenChange={setRegisterOpen}
          session={sessionMeta}
          question={question}
          setQuestion={setQuestion}
          onRegistered={() => setRegisteredCount((c) => (c === null ? 1 : c + 1))}
        />
      )}

      {/* PREVIOUS */}
      <section className="mx-auto max-w-4xl px-5 pt-14 pb-24 md:pt-20 md:pb-28">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <h2 className="font-display text-2xl md:text-3xl font-semibold">⏮️ Previous Free Visa Sessions</h2>
            <p className="mt-1 text-sm text-foreground/60">
              Every completed session, archived automatically once its date passes.
            </p>
          </div>

          {/* Search Bar */}
          <div className="relative w-full md:w-72">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search topics or keywords..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-xs rounded-full border border-border bg-card focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>
        </div>

        {/* Filter Pills */}
        <div className="mt-4 flex flex-wrap gap-2">
          {["All", "Refusal Recovery", "DS-160 Audits", "Funding & Loans", "Mock Drills"].map((tag) => (
            <button
              key={tag}
              type="button"
              onClick={() => setSelectedTag(tag)}
              className={`px-3 py-1 text-xs rounded-full border transition ${
                selectedTag === tag
                  ? "bg-primary text-primary-foreground border-primary font-medium"
                  : "bg-card text-muted-foreground border-border hover:border-primary/50"
              }`}
            >
              {tag}
            </button>
          ))}
        </div>

        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          {loading && (
            <div className="col-span-full rounded-2xl border border-dashed border-border p-10 grid place-items-center">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          )}
          {!loading && filteredPrevious.length === 0 && (
            <div className="col-span-full rounded-2xl border border-dashed border-border p-8 text-center text-muted-foreground text-sm">
              No sessions matched your search or filter.
            </div>
          )}
          {filteredPrevious.map((s) => (
            <PastSessionCard key={s.id} session={s} />
          ))}
        </div>

        <TopicPoll />

        {/* 1-ON-1 BRIDGE */}
        <div className="mt-12 rounded-3xl border border-primary/30 bg-primary/5 p-6 md:p-8 shadow-sm">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 text-primary px-3 py-1 text-[11px] font-bold uppercase tracking-wider">
            Need individual attention?
          </span>
          <h3 className="mt-3 font-display text-xl md:text-2xl font-semibold">
            Have a complex profile or a prior visa refusal?
          </h3>
          <p className="mt-2 text-sm md:text-base text-muted-foreground leading-relaxed max-w-2xl">
            Group sessions cover general strategies. For line-by-line DS-160 auditing and personalized mock simulations,
            explore our 1-on-1 Support Plans.
          </p>

          <div className="mt-4 flex flex-wrap gap-2">
            {["Gap Years", "Prior 214(b) Refusals", "Multiple Sponsors", "Backlogs / Low GRE"].map((tag) => (
              <span
                key={tag}
                className="rounded-full border border-border bg-card px-3 py-1 text-[11px] font-medium text-muted-foreground"
              >
                {tag}
              </span>
            ))}
          </div>

          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <Link
              to="/find-your-plan"
              onClick={() => trackEvent("free_session_bridge_click", { target: "find_your_plan" })}
              className="inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-gradient-cta text-primary-foreground px-6 py-3 text-sm font-bold shadow-glow hover:opacity-90 transition"
            >
              Find Your Plan (60-Sec Quiz) <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/pricing"
              onClick={() => trackEvent("free_session_bridge_click", { target: "pricing" })}
              className="inline-flex flex-1 items-center justify-center gap-2 rounded-full border border-border bg-card px-6 py-3 text-sm font-semibold hover:border-primary/50 hover:text-primary transition"
            >
              View 1-on-1 Plans &amp; Pricing (From ₹3,000)
            </Link>
          </div>
        </div>
      </section>

      {/* STICKY MOBILE CTA */}
      {sessionMeta && (
        <div className="sm:hidden fixed inset-x-0 bottom-0 z-40 border-t border-border bg-background/95 backdrop-blur px-4 py-3">
          <Button
            size="lg"
            onClick={() => {
              trackEvent("free_session_cta_click", {
                location: "sticky_mobile",
                session_id: sessionMeta.id,
                session_title: sessionMeta.title,
              });
              setRegisterOpen(true);
            }}
            className="w-full rounded-full bg-gradient-cta text-primary-foreground py-6 text-base font-bold"
          >
            Reserve My Free Spot Now <ArrowRight className="ml-1 h-4 w-4" />
          </Button>
        </div>
      )}
    </main>
  );
}

const HOST_NAME = "Senior Visa Coaches(US Alumni)";

function sessionCategory(session: SessionRow): string {
  const t = `${session.title} ${session.description ?? ""}`.toLowerCase();
  if (t.includes("refusal") || t.includes("214(b)")) return "Refusal Recovery";
  if (t.includes("ds-160") || t.includes("ds160")) return "DS-160 Audits";
  if (t.includes("fund") || t.includes("loan") || t.includes("sponsor") || t.includes("bank")) return "Funding & Loans";
  if (t.includes("mock") || t.includes("drill")) return "Mock Drills";
  return "Visa Prep";
}

function PastSessionCard({ session }: { session: SessionRow }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const d = session.starts_at ? new Date(session.starts_at) : null;
  const rawDesc = stripArchiveTag(session.description);
  const parsedContent = parseSessionDescription(rawDesc);

  return (
    <article className="flex flex-col justify-between rounded-2xl border border-border bg-card p-6 shadow-sm transition hover:shadow-md hover:border-primary/40">
      <div>
        {/* Top Header */}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Badge
            variant="secondary"
            className="bg-primary/10 text-primary gap-1 border-none text-[10px] font-semibold tracking-wider uppercase"
          >
            {sessionCategory(session)}
          </Badge>
          {d && (
            <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground font-medium">
              <Calendar className="h-3.5 w-3.5 text-primary" />
              {format(d, "EEE, d MMM yyyy")}
            </span>
          )}
        </div>

        {/* Title */}
        <h3 className="mt-3 font-display text-lg font-semibold leading-tight text-foreground">{session.title}</h3>

        <p className="mt-1.5 inline-flex items-start gap-1.5 text-xs text-muted-foreground">
          <GraduationCap className="h-3.5 w-3.5 mt-[1px] shrink-0 text-primary" />
          Hosted by {HOST_NAME}
        </p>

        {/* Overview */}
        {parsedContent.overview && (
          <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{parsedContent.overview}</p>
        )}

        {/* Bullet Topics */}
        {parsedContent.bullets.length > 0 && (
          <div className="mt-4 space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wider text-primary">Key Topics Covered</p>
            <ul className="space-y-1.5 text-sm text-foreground/90">
              {(isExpanded ? parsedContent.bullets : parsedContent.bullets.slice(0, 3)).map((bullet, idx) => (
                <li key={idx} className="flex items-start gap-2">
                  <span className="text-primary font-bold mt-0.5">•</span>
                  <span className="leading-snug">{bullet}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Accordion Toggle */}
        {parsedContent.bullets.length > 3 && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="mt-3 h-8 px-0 text-xs text-primary hover:bg-transparent hover:text-primary/80"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? (
              <>
                Show less <ChevronUp className="ml-1 h-3.5 w-3.5" />
              </>
            ) : (
              <>
                View all topics ({parsedContent.bullets.length}) <ChevronDown className="ml-1 h-3.5 w-3.5" />
              </>
            )}
          </Button>
        )}
      </div>
    </article>
  );
}

function parseSessionDescription(text: string) {
  if (!text) return { overview: "", bullets: [] };

  const lines = text
    .split(/\n|•|✅|🔹|\*|\r/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0 && !line.startsWith("http"));

  if (lines.length === 0) return { overview: "", bullets: [] };

  const overview = lines[0];
  const bullets = lines
    .slice(1)
    .map((b) => b.replace(/^[-–—:•*✅🔹🚀]\s*/, "").trim())
    .filter((b) => b.length > 3);

  return { overview, bullets };
}

type SessionMeta = {
  id: string | null;
  title: string;
  startsAt: string | null;
  dateLabel: string;
  timeLabel: string;
  joinUrl: string | null;
  durationMinutes: number;
  topics: string[];
};

const DEFAULT_TOPICS = [
  "How to answer 214(b) 'strong ties' questions",
  "Explaining loans, sponsors & funding proof",
  "Avoiding DS-160 mismatches that trigger refusals",
];

function whatsappShareUrl(s: SessionMeta): string {
  const topics = (s.topics.length ? s.topics : DEFAULT_TOPICS).slice(0, 3);
  const text =
    `🎟️ Just reserved my free slot for WeLivdIt's Sunday Visa Masterclass (7 PM IST).\n\n` +
    `📌 Session: ${s.title}\n\nSenior coaches are doing live DS-160 audits and mock drills on Google Meet:\n` +
    topics.map((t) => `• ${t}`).join("\n") +
    `\n\nJoin free here: https://welivdit.com/freevisasessions`;
  return `https://wa.me/?text=${encodeURIComponent(text)}`;
}

const TOPIC_SUGGESTIONS = ["214(b) Re-attempts", "Loan & Funding Proof", "DS-160 Mismatches", "Tie to Home Country"];

type PublicPoll = Awaited<ReturnType<typeof getPublicPoll>>;
let pollPromise: Promise<PublicPoll> | null = null;
function usePublicPoll() {
  const [data, setData] = useState<PublicPoll | null>(null);
  useEffect(() => {
    pollPromise ??= getPublicPoll().catch(() => ({ trending: [], revealed: false, poll: null, results: null }));
    let alive = true;
    void pollPromise.then((d) => alive && setData(d));
    return () => {
      alive = false;
    };
  }, []);
  return data;
}

function TrendingTopics({ onPick, compact = false }: { onPick: (t: string) => void; compact?: boolean }) {
  const data = usePublicPoll();
  const real = data?.trending ?? [];
  const labels = real.length ? real.map((t) => t.label) : TOPIC_SUGGESTIONS;
  if (compact) {
    return (
      <div className="mt-2 flex flex-wrap gap-1.5">
        {labels.map((label) => (
          <button
            key={label}
            type="button"
            onClick={() => onPick(label)}
            className="rounded-full border border-border bg-card px-2.5 py-1 text-[11px] hover:border-primary/50 hover:text-primary transition"
          >
            + {label}
          </button>
        ))}
      </div>
    );
  }
  return (
    <div className="rounded-2xl border border-border bg-card p-5 text-left">
      <p className="font-semibold">🔥 {real.length ? "Top Trending Questions This Week" : "Popular Question Topics"}</p>
      <p className="text-xs text-muted-foreground">Tap a topic to add it to your question.</p>
      {real.length ? (
        <div className="mt-4 space-y-2.5">
          {real.map((t) => (
            <button
              key={t.label}
              type="button"
              onClick={() => onPick(t.label)}
              className="group block w-full text-left"
            >
              <div className="flex justify-between text-xs font-medium">
                <span className="group-hover:text-primary transition">{t.label}</span>
                <span className="text-muted-foreground">{t.pct}%</span>
              </div>
              <div className="mt-1 h-2 rounded-full bg-muted overflow-hidden">
                <div className="h-full rounded-full bg-primary" style={{ width: `${t.pct}%` }} />
              </div>
            </button>
          ))}
        </div>
      ) : (
        <div className="mt-3 flex flex-wrap gap-2">
          {labels.map((label) => (
            <button
              key={label}
              type="button"
              onClick={() => onPick(label)}
              className="rounded-full border border-border bg-background px-3 py-1.5 text-xs hover:border-primary/50 hover:text-primary transition"
            >
              {label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function TopicPoll() {
  const initial = usePublicPoll();
  const vote$ = useServerFn(castTopicVote);
  const [choice, setChoice] = useState<string | null>(null);
  const [voted, setVoted] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState<PublicPoll["results"]>(null);
  const poll = initial?.poll ?? null;
  const voteKey = poll ? `wl_topic_vote:${poll.id}` : null;
  useEffect(() => {
    if (!voteKey) return;
    try {
      setVoted(localStorage.getItem(voteKey));
    } catch {
      /* ignore */
    }
  }, [voteKey]);
  useEffect(() => {
    if (initial?.results) setResults(initial.results);
  }, [initial]);

  if (!poll) return null;

  const vote = async () => {
    if (!choice) return;
    setSubmitting(true);
    try {
      const res = await vote$({ data: { pollId: poll.id, topic: choice } });
      trackEvent("free_session_topic_vote", { topic: choice });
      try {
        localStorage.setItem(`wl_topic_vote:${poll.id}`, choice);
      } catch {
        /* ignore */
      }
      setResults(res.results);
      setVoted(choice);
    } catch (e) {
      toast.error("Couldn't record your vote", { description: e instanceof Error ? e.message : "" });
    } finally {
      setSubmitting(false);
    }
  };

  const fmt = (iso: string | null) =>
    iso
      ? new Date(iso).toLocaleString("en-IN", {
          timeZone: "Asia/Kolkata",
          weekday: "short",
          day: "numeric",
          month: "short",
          hour: "numeric",
          minute: "2-digit",
        }) + " IST"
      : "";
  const statusLine =
    poll.status === "scheduled"
      ? `🕒 Voting opens ${fmt(poll.opensAt)}`
      : poll.status === "closed"
        ? results
          ? "🔒 Voting closed · final results"
          : "🔒 Voting closed · results coming soon"
        : poll.closesAt
          ? `🟢 Voting open · closes ${fmt(poll.closesAt)}`
          : "🟢 Voting open";

  const Results = results ? (
    <div className="mt-4 space-y-2.5">
      {results.counts.map((c) => (
        <div key={c.topic}>
          <div className="flex justify-between text-xs font-medium">
            <span className={voted === c.topic ? "text-primary" : ""}>
              {c.topic} {voted === c.topic && "✓"}
            </span>
            <span className="text-muted-foreground">{c.pct}%</span>
          </div>
          <div className="mt-1 h-2 rounded-full bg-muted overflow-hidden">
            <div className="h-full rounded-full bg-primary" style={{ width: `${c.pct}%` }} />
          </div>
        </div>
      ))}
      <p className="pt-1 text-[11px] text-muted-foreground">{results.total} votes</p>
    </div>
  ) : null;

  return (
    <div className="mt-10 mx-auto max-w-md rounded-2xl border border-border bg-card p-5">
      <p className="font-semibold">🗳️ {poll.title}</p>
      <p className="mt-0.5 text-xs text-muted-foreground">{statusLine}</p>
      {poll.status !== "open" ? (
        (Results ?? (
          <ul className="mt-3 space-y-1.5 text-sm text-muted-foreground">
            {poll.options.map((o) => (
              <li key={o}>• {o}</li>
            ))}
          </ul>
        ))
      ) : voted ? (
        (Results ?? (
          <p className="mt-3 text-sm text-muted-foreground">
            ✅ Thanks! You voted for <span className="font-medium text-foreground">{voted}</span>. Results will be
            shared here once voting closes.
          </p>
        ))
      ) : (
        <>
          <div className="mt-3 space-y-2">
            {poll.options.map((label) => (
              <label
                key={label}
                className={`flex items-center gap-2.5 rounded-xl border px-3 py-2 text-sm cursor-pointer transition ${
                  choice === label ? "border-primary bg-primary/5" : "border-border"
                }`}
              >
                <input type="radio" name="topic-poll" checked={choice === label} onChange={() => setChoice(label)} />
                {label}
              </label>
            ))}
          </div>
          <Button onClick={vote} disabled={!choice || submitting} className="mt-4 w-full rounded-full">
            {submitting && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
            Vote &amp; View Results
          </Button>
        </>
      )}
    </div>
  );
}

function googleCalendarUrl(s: SessionMeta): string {
  if (!s.startsAt) return "";
  const start = new Date(s.startsAt);
  const end = new Date(start.getTime() + s.durationMinutes * 60_000);
  const fmt = (d: Date) =>
    d
      .toISOString()
      .replace(/[-:]/g, "")
      .replace(/\.\d{3}/, "");
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: `WeLivdIt Live: ${s.title}`,
    dates: `${fmt(start)}/${fmt(end)}`,
    details: s.joinUrl
      ? `Free F-1 visa prep session by WeLivdIt.\nJoin: ${s.joinUrl}`
      : "Free F-1 visa prep session by WeLivdIt. Joining link will be emailed to you.",
    location: s.joinUrl || "Google Meet",
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

function RegistrationModal({
  open,
  onOpenChange,
  session,
  question,
  setQuestion,
  onRegistered,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  session: SessionMeta;
  question: string;
  setQuestion: (v: string) => void;
  onRegistered?: () => void;
}) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState<string | null>(null);
  const [website, setWebsite] = useState("");
  const openedAt = useRef<number>(Date.now());
  useEffect(() => {
    if (open) openedAt.current = Date.now();
  }, [open]);
  const appendTopic = (t: string) => {
    const cur = question.trim();
    if (cur.toLowerCase().includes(t.toLowerCase())) return;
    setQuestion(cur ? `${cur}; ${t}` : t);
  };

  const register = useServerFn(registerForSession);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const cleanEmail = email.trim().toLowerCase();
    if (!name.trim()) {
      toast.error("Please enter your name");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail)) {
      toast.error("Please enter a valid email");
      return;
    }
    const { data: sess } = await supabase.auth.getSession();
    if (!sess.session) {
      toast.error("Please sign in to register", { description: "We use your signed-in email for the invite." });
      window.location.href = "/auth";
      return;
    }
    setSubmitting(true);
    try {
      await register({
        data: {
          name: name.trim(),
          email: cleanEmail,
          phone: phone.trim() || null,
          question: question.trim() || null,
          sessionId: session.id,
          sessionTitle: session.title,
          dateLabel: session.dateLabel,
          timeLabel: session.timeLabel,
          startsAt: session.startsAt,
          website,
          elapsedMs: Date.now() - openedAt.current,
          referralSource: getReferralSource(),
        },
      });
      trackEvent("free_session_registered", {
        session_id: session.id,
        session_title: session.title,
        has_phone: Boolean(phone.trim()),
      });
      setRegisteredEmail(cleanEmail);
      onRegistered?.();
    } catch (err) {
      toast.error("Couldn't complete your registration", {
        description: err instanceof Error ? err.message : "Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const downloadInvite = () => {
    if (!session.startsAt) return;
    trackEvent("free_session_calendar_ics", { session_id: session.id, session_title: session.title });
    const ics = buildIcs({
      uid: `free-session-${new Date(session.startsAt).getTime()}`,
      title: `WeLivdIt Live: ${session.title}`,
      description: session.joinUrl
        ? `Free F-1 visa prep session by WeLivdIt.\nJoin: ${session.joinUrl}`
        : "Free F-1 visa prep session by WeLivdIt. Joining link will be emailed to you.",
      startsAt: new Date(session.startsAt),
      durationMinutes: session.durationMinutes,
      location: session.joinUrl || "Google Meet",
      url: session.joinUrl || "https://welivdit.com/freevisasessions",
    });
    downloadIcs("welivdit-live-session.ics", ics);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        {registeredEmail ? (
          <div>
            <DialogHeader>
              <DialogTitle className="text-left">🎉 You&apos;re Registered!</DialogTitle>
              <DialogDescription className="text-left">
                We&apos;ve sent your confirmation email with the access link to{" "}
                <span className="font-medium text-foreground">{registeredEmail}</span>.
              </DialogDescription>
            </DialogHeader>

            <div className="mt-5 space-y-3 rounded-2xl border border-border bg-card p-4 text-sm">
              <div>
                <p className="text-[11px] font-bold uppercase tracking-wider text-primary">🗓️ When</p>
                <p className="mt-0.5 font-semibold">{session.title}</p>
                <p className="text-muted-foreground">
                  {session.dateLabel} at {session.timeLabel} · {session.durationMinutes} min
                </p>
              </div>
              <div className="border-t border-border pt-3">
                <p className="text-[11px] font-bold uppercase tracking-wider text-primary">🎥 Google Meet access</p>
                {session.joinUrl ? (
                  <>
                    <a
                      href={session.joinUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-0.5 block break-all font-medium text-primary underline-offset-2 hover:underline"
                    >
                      {session.joinUrl}
                    </a>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Join 5 minutes early. The same link is in your confirmation email.
                    </p>
                  </>
                ) : (
                  <p className="mt-0.5 text-muted-foreground">
                    Your Google Meet link will be emailed to you before the session starts.
                  </p>
                )}
              </div>
              <div className="border-t border-border pt-3">
                <p className="text-[11px] font-bold uppercase tracking-wider text-primary">✋ Prepare your questions</p>
                <p className="mt-0.5 text-muted-foreground leading-relaxed">
                  Note down your doubts (214(b), funding, DS-160) before Sunday. Use Google Meet&apos;s &quot;Raise
                  Hand&quot; during the call to ask questions or volunteer for a live mock.
                </p>
              </div>
            </div>

            <div className="mt-4 space-y-2">
              <a
                href={googleCalendarUrl(session)}
                target="_blank"
                rel="noreferrer"
                onClick={() =>
                  trackEvent("free_session_calendar_google", {
                    session_id: session.id,
                    session_title: session.title,
                  })
                }
                className="flex w-full items-center justify-center gap-2 rounded-full border border-border bg-card px-4 py-2.5 text-sm font-medium hover:border-primary/50 transition"
              >
                <Calendar className="h-4 w-4 text-primary" /> 📅 Add to Google Calendar
              </a>
              <button
                type="button"
                onClick={downloadInvite}
                className="flex w-full items-center justify-center gap-2 rounded-full border border-border bg-card px-4 py-2.5 text-sm font-medium hover:border-primary/50 transition"
              >
                <Download className="h-4 w-4 text-primary" /> 📆 Download Outlook / Apple (.ics)
              </button>
            </div>

            <a
              href={whatsappShareUrl(session)}
              target="_blank"
              rel="noreferrer"
              onClick={() => trackEvent("free_session_share_whatsapp", { session_id: session.id })}
              className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-gradient-cta text-primary-foreground px-4 py-3 text-sm font-bold shadow-glow hover:opacity-90 transition"
            >
              📲 Share Session with your friends
            </a>

            <div className="mt-6 rounded-2xl border border-primary/30 bg-primary/5 p-5">
              <p className="font-semibold">Need 1-on-1 Help Before Sunday?</p>
              <p className="mt-1 text-sm text-muted-foreground leading-relaxed">
                Don&apos;t wait for group Q&amp;A. Book a private 1-on-1 mock interview with an expert coach to review
                your DS-160 line-by-line.
              </p>
              <Link
                to="/pricing"
                onClick={() => onOpenChange(false)}
                className="mt-4 inline-flex items-center gap-2 rounded-full bg-gradient-cta text-primary-foreground px-5 py-2.5 text-sm font-semibold hover:opacity-90 transition"
              >
                View 1-on-1 Private Plans (From ₹3,000) <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        ) : (
          <form onSubmit={submit}>
            <DialogHeader>
              <DialogTitle className="text-left">Reserve your free spot</DialogTitle>
              <DialogDescription className="text-left">
                {session.title} · {session.dateLabel} at {session.timeLabel}
              </DialogDescription>
            </DialogHeader>

            <div className="mt-5 space-y-3">
              <div>
                <label htmlFor="reg-name" className="text-xs font-medium text-muted-foreground">
                  Name *
                </label>
                <input
                  id="reg-name"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={100}
                  placeholder="Your full name"
                  className="mt-1 w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>
              <div>
                <label htmlFor="reg-email" className="text-xs font-medium text-muted-foreground">
                  Email address *
                </label>
                <input
                  id="reg-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  maxLength={255}
                  placeholder="you@email.com"
                  className="mt-1 w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>
              <div>
                <label htmlFor="reg-phone" className="text-xs font-medium text-muted-foreground">
                  Phone / WhatsApp (optional)
                </label>
                <input
                  id="reg-phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  maxLength={30}
                  placeholder="+91 98765 43210"
                  className="mt-1 w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>
              <div>
                <label htmlFor="reg-question" className="text-xs font-medium text-muted-foreground">
                  Got a specific question or refusal reason for this Sunday&apos;s live session? (Optional)
                </label>
                <TrendingTopics onPick={appendTopic} compact />
                <textarea
                  id="reg-question"
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  maxLength={1000}
                  rows={3}
                  placeholder="e.g. Refused under 214(b) in Hyderabad - how do I explain my funding?"
                  className="mt-2 w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>
            </div>

            {/* Honeypot */}
            <div aria-hidden="true" className="absolute left-[-9999px] h-0 w-0 overflow-hidden">
              <label htmlFor="reg-website">Website</label>
              <input
                id="reg-website"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-gradient-cta text-primary-foreground px-6 py-3 text-sm font-semibold hover:opacity-90 transition disabled:opacity-60"
            >
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              {submitting ? "Reserving…" : "Confirm My Free Spot →"}
            </button>
            <p className="mt-2 text-center text-[11px] text-muted-foreground">
              We&apos;ll email your joining link and calendar invite right away.
            </p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
